/**
 * 구성원 엑셀 일괄등록
 * ---------------------------------------------------------------------------
 * 세 걸음으로 나눠 둔다. 서버 쪽 설명(member-bulk.service.ts)과 같은 이유다 —
 * **되돌릴 수 없는 일 앞에서 먼저 보여 준다.**
 *
 *   1. 서식 받기   그 기업의 부서·직책이 적힌 파일을 내려받는다.
 *   2. 확인하기    올리면 무엇이 틀렸는지, 요금이 얼마나 늘어나는지 보여 준다.
 *                  이 단계에서는 아무도 만들어지지 않는다.
 *   3. 등록하기    동의에 체크해야 단추가 열린다.
 *
 * 같은 파일을 2단계와 3단계에서 두 번 보낸다. 미리 본 결과를 들고 있다가
 * 쓰지 않는 이유는, 그 사이에 다른 관리자가 같은 이메일을 등록했을 수
 * 있기 때문이다. 서버가 등록 직전에 처음부터 다시 검증한다.
 */

import { useState } from 'react';
import {
  Alert,
  App as AntdApp,
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Input,
  Modal,
  Result,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  InboxOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

/* =========================================================================
 * 서버가 주는 모양
 * ====================================================================== */

interface RowIssue {
  row: number;
  column: string;
  message: string;
}

interface SeatAffected {
  productName: string;
  planName: string;
  unitLabel: string;
  contractedUnits: number;
  toUnits: number;
  delta: number;
}

interface BulkPreview {
  fileName: string;
  rowCount: number;
  validCount: number;
  issues: RowIssue[];
  preview: Array<{
    row: number;
    name: string;
    email: string;
    empNo: string | null;
    departmentName: string | null;
    positionName: string | null;
    employmentTypeName: string;
  }>;
  seats: {
    activeUsers: number;
    requiredUnits: number;
    allowed: boolean;
    approvable: boolean;
    monthlyDelta: number;
    affected: SeatAffected[];
    blocked: Array<{ productName: string; planName: string; maxUnits: number | null }>;
    message: string | null;
  };
  consent: { version: string; text: string };
}

interface BulkResult {
  createdCount: number;
  failedCount: number;
  failures: RowIssue[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** 등록이 끝나 목록을 다시 받아야 할 때. */
  onDone: () => void;
}

/* =========================================================================
 * 화면
 * ====================================================================== */

export default function MemberBulkModal({ open, onClose, onDone }: Props) {
  const { message } = AntdApp.useApp();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  const [consent, setConsent] = useState(false);
  const [overageReason, setOverageReason] = useState('');

  const [busy, setBusy] = useState(false);

  const step = result ? 2 : preview ? 1 : 0;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setConsent(false);
    setOverageReason('');
  };

  const close = () => {
    reset();
    onClose();
  };

  /* ---- 1. 서식 받기 --------------------------------------------------- */

  const downloadTemplate = async () => {
    setBusy(true);
    try {
      const res = await apiClient.get('/users/bulk/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '구성원_일괄등록_서식.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(errorText(error as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  /* ---- 2. 확인하기 ----------------------------------------------------- */

  const runPreview = async (picked: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', picked);
      const { data } = await apiClient.post<BulkPreview>('/users/bulk/preview', form);
      setFile(picked);
      setPreview(data);
    } catch (error) {
      message.error(errorText(error as NormalizedError));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  /* ---- 3. 등록하기 ----------------------------------------------------- */

  const commit = async () => {
    if (!file || !preview) return;

    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('consentAgreed', 'true');
      if (overageReason.trim()) form.append('seatOverageReason', overageReason.trim());

      const { data } = await apiClient.post<BulkResult>('/users/bulk/commit', form);
      setResult(data);
      if (data.createdCount > 0) onDone();
    } catch (error) {
      message.error(errorText(error as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  /* ---- 계약 인원 ------------------------------------------------------- */

  const seats = preview?.seats;
  const needsApproval = Boolean(seats && !seats.allowed && seats.approvable);
  const blocked = Boolean(seats && seats.blocked.length > 0);

  const canCommit =
    Boolean(preview) &&
    preview!.validCount > 0 &&
    consent &&
    !blocked &&
    (!needsApproval || overageReason.trim().length > 0);

  return (
    <Modal
      open={open}
      onCancel={close}
      title="구성원 엑셀 일괄등록"
      width={880}
      maskClosable={false}
      footer={
        result ? (
          <Button type="primary" onClick={close}>
            닫기
          </Button>
        ) : (
          <Space>
            <Button onClick={close}>취소</Button>
            {preview && (
              <Button
                onClick={() => {
                  setPreview(null);
                  setFile(null);
                  setConsent(false);
                }}
              >
                다른 파일 올리기
              </Button>
            )}
            <Button type="primary" loading={busy} disabled={!canCommit} onClick={commit}>
              {preview ? `${preview.validCount}명 등록` : '등록'}
            </Button>
          </Space>
        )
      }
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[{ title: '서식 받기' }, { title: '확인하기' }, { title: '등록 완료' }]}
      />

      {/* ------------------------------------------------ 1단계 */}
      {step === 0 && (
        <>
          <Alert
            type="info"
            showIcon
            message="서식을 먼저 내려받으세요"
            description={
              <>
                내려받은 파일의 「코드표」 시트에 <b>이 회사에 등록된 부서·직책 이름</b>이
                들어 있습니다. 그 이름을 그대로 적어야 올라갑니다. 부서가 아직 없으면
                비워 두고 올린 뒤 나중에 지정해도 됩니다.
              </>
            }
            style={{ marginBottom: 16 }}
          />

          <Button
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
            loading={busy}
            style={{ marginBottom: 20 }}
          >
            서식 내려받기 (.xlsx)
          </Button>

          <Upload.Dragger
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            disabled={busy}
            // antd 가 자기 방식으로 보내지 않게 막는다. 우리가 직접 보낸다.
            beforeUpload={(picked) => {
              void runPreview(picked as unknown as File);
              return false;
            }}
            fileList={[] as UploadFile[]}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">작성한 엑셀 파일을 여기에 끌어다 놓으세요</p>
            <p className="ant-upload-hint">
              올려도 바로 등록되지 않습니다. 먼저 내용을 확인해 드립니다.
            </p>
          </Upload.Dragger>
        </>
      )}

      {/* ------------------------------------------------ 2단계 */}
      {step === 1 && preview && (
        <>
          <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="파일">
              <Space>
                <FileExcelOutlined />
                {preview.fileName}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="읽은 줄">{preview.rowCount}줄</Descriptions.Item>
            <Descriptions.Item label="등록될 인원">
              <Text strong>{preview.validCount}명</Text>
              {preview.issues.length > 0 && (
                <Text type="danger"> (오류 {preview.issues.length}건 제외)</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* ---- 계약 인원 ---- */}
          {seats && (
            <Alert
              type={blocked ? 'error' : needsApproval ? 'warning' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
              message={
                blocked
                  ? '요금제 한도를 넘습니다'
                  : needsApproval
                    ? '계약 인원을 넘깁니다'
                    : '계약 인원 안에서 등록됩니다'
              }
              description={
                <>
                  <div>
                    지금 {seats.activeUsers}명 → 등록 후 <b>{seats.requiredUnits}명</b>
                  </div>

                  {blocked && (
                    <div style={{ marginTop: 8 }}>
                      {seats.blocked.map((b) => (
                        <div key={b.productName}>
                          {b.productName}의 「{b.planName}」 는 최대 {b.maxUnits}명까지입니다.
                          상위 요금제로 바꾼 뒤 다시 올려 주세요.
                        </div>
                      ))}
                    </div>
                  )}

                  {needsApproval && (
                    <>
                      <div style={{ marginTop: 8 }}>
                        {seats.affected.map((a) => (
                          <div key={a.productName}>
                            {a.productName} · {a.planName} — 계약 {a.contractedUnits}
                            {a.unitLabel} → {a.toUnits}
                            {a.unitLabel}, 월 <b>{a.delta.toLocaleString()}원</b> 증가
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        합계 월 <b>{seats.monthlyDelta.toLocaleString()}원</b> 증가
                        (부가세 별도)
                      </div>
                      <Input.TextArea
                        rows={2}
                        placeholder="늘리는 사유를 적어 주세요 (예: 2026년 상반기 신입사원 입사)"
                        value={overageReason}
                        onChange={(e) => setOverageReason(e.target.value)}
                        maxLength={500}
                        style={{ marginTop: 12 }}
                      />
                    </>
                  )}
                </>
              }
            />
          )}

          {/* ---- 오류 ---- */}
          {preview.issues.length > 0 && (
            <>
              <Divider orientation="left" style={{ marginTop: 8 }}>
                <Space>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  고쳐야 할 줄 {preview.issues.length}건
                </Space>
              </Divider>
              <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                아래 줄은 <b>등록되지 않습니다.</b> 나머지만 먼저 등록하고 이 줄들은
                고쳐서 다시 올려도 되고, 지금 파일을 고쳐 다시 올려도 됩니다.
              </Paragraph>
              <Table
                size="small"
                rowKey={(r) => `${r.row}-${r.column}-${r.message}`}
                dataSource={preview.issues}
                pagination={preview.issues.length > 8 ? { pageSize: 8 } : false}
                columns={[
                  { title: '줄', dataIndex: 'row', width: 60 },
                  {
                    title: '칸',
                    dataIndex: 'column',
                    width: 100,
                    render: (v: string) => (v ? <Tag>{v}</Tag> : null),
                  },
                  { title: '무엇이 문제인가', dataIndex: 'message' },
                ]}
                style={{ marginBottom: 16 }}
              />
            </>
          )}

          {/* ---- 등록될 사람 ---- */}
          {preview.validCount > 0 && (
            <>
              <Divider orientation="left">등록될 {preview.validCount}명</Divider>
              <Table
                size="small"
                rowKey="row"
                dataSource={preview.preview}
                pagination={preview.preview.length > 8 ? { pageSize: 8 } : false}
                columns={[
                  { title: '줄', dataIndex: 'row', width: 56 },
                  { title: '이름', dataIndex: 'name', width: 90 },
                  { title: '이메일', dataIndex: 'email', ellipsis: true },
                  { title: '사번', dataIndex: 'empNo', width: 90 },
                  { title: '부서', dataIndex: 'departmentName', width: 120 },
                  { title: '직책', dataIndex: 'positionName', width: 90 },
                  { title: '고용형태', dataIndex: 'employmentTypeName', width: 90 },
                ]}
                style={{ marginBottom: 16 }}
              />
            </>
          )}

          {/* ---- 동의 ---- */}
          <Alert
            type="warning"
            style={{ marginBottom: 8 }}
            message={
              <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)}>
                <Text strong>개인정보 수집·이용에 대한 확인 (필수)</Text>
              </Checkbox>
            }
            description={
              <Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
                {preview.consent.text}
              </Paragraph>
            }
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            동의하신 사실과 문구는 기록으로 남고, 기업관리 화면에서 확인할 수 있습니다.
          </Text>
        </>
      )}

      {/* ------------------------------------------------ 3단계 */}
      {step === 2 && result && (
        <Result
          status={result.failedCount === 0 ? 'success' : 'warning'}
          title={`${result.createdCount}명을 등록했습니다`}
          subTitle={
            result.createdCount > 0
              ? '등록된 분들께 초대 메일이 발송되었습니다. 메일의 링크로 비밀번호를 정하면 로그인할 수 있습니다.'
              : '등록된 사람이 없습니다.'
          }
        >
          {result.failures.length > 0 && (
            <>
              <Paragraph>
                <Text strong>{result.failedCount}건은 등록하지 못했습니다.</Text> 아래 줄만
                고쳐서 다시 올려 주세요 — 이미 등록된 분들은 다시 올려도 중복으로
                걸러집니다.
              </Paragraph>
              <Table
                size="small"
                rowKey={(r) => `${r.row}-${r.column}-${r.message}`}
                dataSource={result.failures}
                pagination={result.failures.length > 10 ? { pageSize: 10 } : false}
                columns={[
                  { title: '줄', dataIndex: 'row', width: 60 },
                  {
                    title: '칸',
                    dataIndex: 'column',
                    width: 100,
                    render: (v: string) => (v ? <Tag>{v}</Tag> : null),
                  },
                  { title: '까닭', dataIndex: 'message' },
                ]}
              />
            </>
          )}
        </Result>
      )}
    </Modal>
  );
}
